import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Waves, Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';

type NavItem =
  | { name: string; href: string; children?: undefined }
  | { name: string; href?: undefined; children: { name: string; href: string }[] };

const navigation: NavItem[] = [
  { name: 'Home', href: '/' },
  {
    name: 'Project',
    children: [
      { name: 'Research', href: '/project/research' },
      { name: 'Publications', href: '/project/publications' },
    ],
  },
  {
    name: 'Community',
    children: [
      { name: 'Events', href: '/community/exhibits' },
      { name: 'FabVan', href: '/community/fabvan' },
    ],
  },
  { name: 'Map', href: '/map' },
  { name: 'Web VR', href: '/webvr' },
  {
    name: 'Education',
    children: [
      { name: 'Lectures', href: '/education/lectures' },
      { name: 'Game-based Learning', href: '/education/game-based-learning' },
    ],
  },
];

export default function Navbar() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (name: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  };

  const toggleMobileSubmenu = (name: string) => {
    setOpenMobileSubmenu((current) => (current === name ? null : name));
  };

  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
    navigate('/register');
  };

  // seawall's access token only guarantees an id/permissions payload — email
  // isn't documented as a claim, so fall back gracefully when it's absent.
  const accountLabel = user?.email ?? (user?.id != null ? `Account #${user.id}` : 'Account');

  return (
    <nav className="fixed left-0 right-0 bg-white/90 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex items-center">
            <a href="/" className="flex items-center">
              <Waves className="h-10 w-10 text-teal-500" />
              <span className="ml-3 text-2xl font-heading font-normal text-[#004F56]">
                BIOCAP
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-4 items-center">
            {navigation.map((item) => {
              if (item.children) {
                const isOpen = openDropdown === item.name;
                return (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.name)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className="flex items-center gap-1 text-gray-600 hover:text-teal-500 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="absolute left-0 top-full mt-1 min-w-[11rem] w-max bg-white rounded-md shadow-lg ring-1 ring-black/5 py-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                            onClick={() => setOpenDropdown(null)}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-600 hover:text-teal-500 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                >
                  {item.name}
                </Link>
              );
            })}

            {!isLoading && isAuthenticated && user ? (
              <>
                <Link
                  to="/sensorbox"
                  className="text-gray-600 hover:text-teal-500 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                >
                  SensorBox
                </Link>
                <span
                  className="text-gray-700 px-2 py-1 text-sm font-medium max-w-[10rem] truncate"
                  title={accountLabel}
                >
                  {accountLabel}
                </span>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="text-gray-600 hover:text-teal-500 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                >
                  Log out
                </button>
              </>
            ) : (
              <Link
                to="/register"
                className="text-gray-600 hover:text-teal-500 px-2 py-1 rounded-md text-sm font-medium transition-colors"
              >
                Register
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-teal-500"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-4 pt-4 pb-6 space-y-1 bg-white">
            {navigation.map((item) => {
              if (item.children) {
                const isOpen = openMobileSubmenu === item.name;
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMobileSubmenu(item.name)}
                      className="w-full flex items-center justify-between text-gray-600 hover:text-teal-500 px-4 py-3 rounded-md text-base font-medium"
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="pl-4">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="block px-4 py-2 text-sm text-gray-600 hover:text-teal-500 rounded-md font-medium"
                            onClick={() => {
                              setIsMenuOpen(false);
                              setOpenMobileSubmenu(null);
                            }}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-600 hover:text-teal-500 block px-4 py-3 rounded-md text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              );
            })}

            {!isLoading && isAuthenticated && user ? (
              <>
                <Link
                  to="/sensorbox"
                  className="text-gray-600 hover:text-teal-500 block px-4 py-3 rounded-md text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  SensorBox
                </Link>
                <p className="px-4 py-2 text-sm text-gray-700 font-medium">{accountLabel}</p>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="w-full text-left text-gray-600 hover:text-teal-500 block px-4 py-3 rounded-md text-base font-medium"
                >
                  Log out
                </button>
              </>
            ) : (
              <Link
                to="/register"
                className="text-gray-600 hover:text-teal-500 block px-4 py-3 rounded-md text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Register
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
